import React, { useState, useImperativeHandle, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const HIGHLIGHT_COLORS = [
    { name: "Yellow", value: "#FFFF00" },
    { name: "Light Green", value: "#CCFFCC" },
    { name: "Cyan", value: "#CCFFFF" },
    { name: "Pink", value: "#FFCCCC" },
    { name: "Orange", value: "#FFCC99" },
    { name: "Purple", value: "#E0CCFF" },
    { name: "Light Gray", value: "#E0E0E0" },
    { name: "Beige", value: "#F5F5DC" },
];

const COLOR_OPTIONS = [
    { name: "Black", value: "black" },
    { name: "White", value: "white" },
    { name: "Red", value: "red" },
    { name: "Blue", value: "blue" },
    { name: "Green", value: "green" },
    { name: "Purple", value: "purple" },
    { name: "Orange", value: "orange" },
    { name: "Gray", value: "gray" },
    { name: "Pink", value: "pink" },
    { name: "Brown", value: "brown" },
];

const FONT_SIZES = [
    "10px", "12px", "14px", "16px", "18px", "20px", "22px", "24px", "26px", "28px", "32px", "36px",
];

const RichTextEditor = React.forwardRef(({ defaultValue, originalContent,height='300px' }, ref) => {
    const [value, setValue] = useState("");
    const [originalValue, setOriginalValue] = useState("");

    const [history, setHistory] = useState([]); // Stack for undo
    const [redoStack, setRedoStack] = useState([]); // Stack for redo
    const quillRef = React.useRef();

    useImperativeHandle(ref, () => ({
        getContent: () => value,
    }));

    React.useEffect(() => {
        if (defaultValue) {
            setValue(defaultValue);
        }
    }, [defaultValue]);
    React.useEffect(() => {
        if (originalContent) {
            setOriginalValue(originalContent);
        }
    }, [originalContent]);
    const handleChange = (content) => {
        setHistory((prev) => [...prev, value]); // Save current value to history
        setRedoStack([]); // Clear redo stack when making a new change
        setValue(content);
    };



    const modules = {
        toolbar: {
            container: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                [{ font: [] }],
                [{ size: FONT_SIZES }],
                [{ color: COLOR_OPTIONS.map((option) => option.value) }],
                [{ background: HIGHLIGHT_COLORS.map((option) => option.value) }],
                ["bold", "italic", "underline", "strike"],
                [{ align: [] }],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
                ["clean"],
                ["undo", "redo", "revert", "ai"]

            ],
        },
    };

    const formats = [
        "header",
        "font",
        "size",
        "color",
        "background",
        "bold",
        "italic",
        "underline",
        "strike",
        "align",
        "list",
        "bullet",
        "link",
        "image",
    ];
    useEffect(() => {
        if (quillRef.current) {
            const toolbar = quillRef.current.getEditor().getModule('toolbar');

            // Override the undo/redo handlers
            toolbar.addHandler('undo', () => {
                const quill = quillRef.current.getEditor();
                quill.history.undo();
            });

            toolbar.addHandler('redo', () => {
                const quill = quillRef.current.getEditor();
                quill.history.redo();
            });
            toolbar.addHandler('revert', () => {
                const quill = quillRef.current.getEditor();
            });
        }
    }, [quillRef.current]);
    // Custom Quill toolbar icons
    useEffect(() => {
        const undoButton = document.querySelector('.ql-undo');
        const redoButton = document.querySelector('.ql-redo');
        const aiButton = document.querySelector('.ql-ai');

        if (undoButton) {
            // Clear button using safer method
            while (undoButton.firstChild) {
              undoButton.removeChild(undoButton.firstChild);
            }
            const undoIcon = document.createElement('span');
            // Use createElement and setAttribute for SVG instead of innerHTML
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('width', '18');
            svg.setAttribute('height', '18');
            svg.setAttribute('viewBox', '0 0 24 24');
            svg.setAttribute('fill', 'none');
            svg.setAttribute('stroke', 'currentColor');
            svg.setAttribute('stroke-width', '2');
            svg.setAttribute('stroke-linecap', 'round');
            svg.setAttribute('stroke-linejoin', 'round');
            const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path1.setAttribute('d', 'M3 7v6h6');
            const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path2.setAttribute('d', 'M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13');
            svg.appendChild(path1);
            svg.appendChild(path2);
            undoIcon.appendChild(svg);
            undoButton.appendChild(undoIcon);

            // Add Undo functionality
            undoButton.addEventListener('click', () => {
                const quill = document.querySelector('.ql-container').__quill;
                if (quill) {
                    quill.history.undo();
                }
            });
        }

        if (redoButton) {
            // Clear button using safer method
            while (redoButton.firstChild) {
              redoButton.removeChild(redoButton.firstChild);
            }
            const redoIcon = document.createElement('span');
            // Use createElement and setAttribute for SVG instead of innerHTML
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('width', '18');
            svg.setAttribute('height', '18');
            svg.setAttribute('viewBox', '0 0 24 24');
            svg.setAttribute('fill', 'none');
            svg.setAttribute('stroke', 'currentColor');
            svg.setAttribute('stroke-width', '2');
            svg.setAttribute('stroke-linecap', 'round');
            svg.setAttribute('stroke-linejoin', 'round');
            const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path1.setAttribute('d', 'M21 7v6h-6');
            const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path2.setAttribute('d', 'M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13');
            svg.appendChild(path1);
            svg.appendChild(path2);
            redoIcon.appendChild(svg);
            redoButton.appendChild(redoIcon);

            // Add Redo functionality
            redoButton.addEventListener('click', () => {
                const quill = document.querySelector('.ql-container').__quill;
                if (quill) {
                    quill.history.redo();
                }
            });
        }
        // if (aiButton) {
        //     const aiIcon = document.createElement('span');
        //     aiIcon.style.cssText = `
        //       display: inline-block;
        //       padding: 4px 8px;
        //       height: 28px;
        //       border: 2px solid currentColor;
        //       border-radius: 4px;
        //       font-weight: bold;
        //       font-family: 'Arial', sans-serif;
        //       font-size: 14px;
        //       text-transform: uppercase;
        //       text-align: center;
        //       cursor: pointer;
        //     `;
        //     aiIcon.textContent = 'AI';
        //     aiIcon.title = 'Perform AI Action';

        //     aiButton.appendChild(aiIcon);

        //     // Add AI functionality

        //     // Add AI functionality
        //     aiButton.addEventListener('click', () => {
        //         // Perform your AI action here
        //         console.log('AI action performed!');
        //     });

        // }

        // Cleanup event listeners on component unmount
        return () => {
            if (undoButton) {
                undoButton.removeEventListener('click', () => { });
            }
            if (redoButton) {
                redoButton.removeEventListener('click', () => { });
            }
        };
    }, []);

    useEffect(() => {
        if (originalContent) {
            const revertButton = document.querySelector('.ql-revert');

            if (revertButton) {
                // Clear button using safer method
                while (revertButton.firstChild) {
                  revertButton.removeChild(revertButton.firstChild);
                }
                const revertIcon = document.createElement('span');
                // Use createElement and setAttribute for SVG instead of innerHTML
                const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.setAttribute('title', 'Revert to original document');
                svg.setAttribute('width', '18');
                svg.setAttribute('height', '18');
                svg.setAttribute('viewBox', '0 0 24 24');
                svg.setAttribute('fill', 'none');
                svg.setAttribute('stroke', 'currentColor');
                svg.setAttribute('stroke-width', '2');
                svg.setAttribute('stroke-linecap', 'round');
                svg.setAttribute('stroke-linejoin', 'round');
                const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path1.setAttribute('d', 'M2.5 2v6h6');
                const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path2.setAttribute('d', 'M2.66 15.57a10 10 0 1 0 .57-8.38');
                svg.appendChild(path1);
                svg.appendChild(path2);
                revertIcon.appendChild(svg);
                revertButton.appendChild(revertIcon);
                // Add revert functionality
                revertButton.addEventListener('click', () => {
                    setValue(originalContent)


                });
                return () => {

                    if (revertButton) {
                        revertButton.removeEventListener('click', () => { });
                    }
                };
            }
        }

    }, [originalContent])

    return (
            <ReactQuill
                theme="snow"

                value={value}
                onChange={handleChange}
                modules={modules}
                formats={formats}
                style={{  height:height}}
            />
    );
});

export default RichTextEditor;
